import type { ReactNode } from 'react';

import { AlertCircle, CircleCheck } from 'lucide-react';
import clsx from 'clsx';

import type { PipelineStepState } from '../../model/brief-generation.types';

const StarLoaderIcon = ({ animate, testId }: { animate: boolean; testId: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={clsx('size-6 shrink-0 text-primary', animate && 'animate-spin')}
    data-testid={testId}
    aria-hidden="true"
  >
    <path d="M12 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 18v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.35" />
    <path d="M2 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
    <path d="M18 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.45" />
    <path
      d="m4.93 4.93 2.83 2.83"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.85"
    />
    <path
      d="m16.24 16.24 2.83 2.83"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.3"
    />
    <path
      d="m19.07 4.93-2.83 2.83"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.55"
    />
    <path
      d="m7.76 16.24-2.83 2.83"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.25"
    />
  </svg>
);

export interface BriefGenerationStepProps {
  label: string;
  state: PipelineStepState;
  retrySlot?: ReactNode;
}

export const BriefGenerationStep = ({ label, state, retrySlot }: BriefGenerationStepProps) => {
  const icon = {
    done: (
      <CircleCheck className="size-6 shrink-0 text-green-600" data-testid="brief-step-icon-done" />
    ),
    running: <StarLoaderIcon animate testId="brief-step-icon-running" />,
    pending: <StarLoaderIcon animate={false} testId="brief-step-icon-pending" />,
    error: (
      <AlertCircle
        className="size-6 shrink-0 text-destructive"
        data-testid="brief-step-icon-error"
      />
    ),
  }[state];

  return (
    <li className="flex w-full items-center gap-4 rounded-lg border border-border bg-background p-4">
      {icon}
      <span className="flex-1 text-base font-medium leading-6 text-foreground">{label}</span>
      {state === 'error' && retrySlot}
    </li>
  );
};
